import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, Spin, Modal, Input, Button, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import './PWJ220Form.css';

import { usePWJ220Mode } from './hooks/usePWJ220Mode';
import { usePWJ220Record } from './hooks/usePWJ220Record';
import { usePWJ220Navigation } from './hooks/usePWJ220Navigation';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2FabricInfo from './components/Tab2FabricInfo';
import Tab3CostInfo from './components/Tab3CostInfo';
import Tab4Remarks from './components/Tab4Remarks';
import WashLabelDetail from './components/WashLabelDetail';
import pwj220Api, { FJ750, FJ751 } from '../../services/pwj220Api';
import { useModulePermission } from '../../hooks/usePermission';
import { BatchAcceptanceBridge, pwj220f1AcceptanceManifest } from '../../features/acceptance';

const { TabPane } = Tabs;

const EMPTY_MASTER: Partial<FJ750> = {
  fj750_01: '',
  fj750_11: '',
  fj750_12: new Date().toISOString(),
  fj750_13: '',
  fj750_14: '',
  fj750_15: 0,
  fj750_161: '',
  fj750_162: '',
  fj750_17: '',
  fj750_171: 0,
  fj750_172: 0,
  fj750_173: 0,
  fj750_174: 0,
  fj750_18: 0,
  fj750_19: '',
  fj750_20: 1,
  fj750_201: '',
  fj750_211: '',
  fj750_212: '',
  fj750_22: '',
  fj750_23: 0,
  fj750_24: '',
  fj750_25: '',
  fj750_311: '',
  fj750_312: '',
  fj750_32: '',
  fj750_33: 0,
  fj750_34: '',
  fj750_35: '',
  fj750_41: '',
  fj750_42: 0,
  fj750_43: '',
  fj750_44: 1,
  fj750_441: '',
  fj750_45: 1,
  fj750_451: 0,
  fj750_452: 0,
  fj750_453: '',
  fj750_46: '',
  fj750_471: 0,
  fj750_472: '',
  fj750_481: 0,
  fj750_482: '',
  fj750_491: 0,
  fj750_492: '',
  fj750_50: '',
  fj750_501: '',
  fj750_51: '',
  fj750_52: '',
  fj750_53: 0,
  fj750_54: '',
  fj750_61: '',
  fj750_611: '',
  fj750_612: '',
  fj750_62: '',
  fj750_63: '',
  fj750_64: '',
  fj750_65: '',
  fj750_76: '',
  fj750_77: null,
  fj750_78: null,
  fj750_79: 0,
  fj750_80: 'N',
  fj750_91: '',
  fj750_92: null,
  fj750_93: '',
};

const PWJ220Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toAdd, toEdit, toView, isEditing } = usePWJ220Mode();
  const { record, loading, load, save, remove, setRecord } = usePWJ220Record();
  const { allIds, currentIdx, hasPrev, hasNext, fetchList, goFirst, goLast, goPrev, goNext, setCurrentById } =
    usePWJ220Navigation();
  const permissions = useModulePermission('pwj220f1');

  const [formData, setFormData] = useState<Partial<FJ750>>(EMPTY_MASTER);
  const [details, setDetails] = useState<Partial<FJ751>[]>([]);
  const [activeTab, setActiveTab] = useState('1');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sourceId, setSourceId] = useState('');

  // Sync record to form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    }
  }, [record]);

  const loadRecord = useCallback(
    async (id: string) => {
      const result = await load(id);
      if (result) setCurrentById(id);
      return result;
    },
    [load, setCurrentById]
  );

  const handleFirst = useCallback(async () => {
    const id = goFirst();
    if (id) await loadRecord(id);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const id = goPrev();
    if (id) await loadRecord(id);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const id = goNext();
    if (id) await loadRecord(id);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const id = goLast();
    if (id) await loadRecord(id);
  }, [goLast, loadRecord]);

  const handleSearch = useCallback(async (kw?: string) => {
    const keyword = kw ?? searchKeyword;
    const ids = await fetchList(keyword || undefined);
    if (ids.length > 0) {
      await loadRecord(ids[0]);
    } else {
      message.info('查無資料');
    }
  }, [searchKeyword, fetchList, loadRecord]);

  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER });
    setDetails([]);
    toAdd();
  }, [toAdd]);

  const handleSave = useCallback(async () => {
    const result = await save(formData, details, mode === 'add');
    if (result) {
      toView();
      fetchList();
    }
  }, [formData, details, mode, save, toView, fetchList]);

  const handleEdit = useCallback(() => {
    if (!record) {
      message.warning('請先選擇一筆資料');
      return;
    }
    toEdit();
  }, [record, toEdit]);

  const handleRevert = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    toView();
  }, [record, toView]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    Modal.confirm({
      title: '刪除確認',
      content: '請確認刪除本資料？',
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const ok = await remove(record.fj750_01!);
        if (ok) {
          setFormData({ ...EMPTY_MASTER });
          setDetails([]);
          setRecord(null);
          toView();
          fetchList();
        }
      },
    });
  }, [record, remove, setRecord, toView, fetchList]);

  const handleExit = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePrint = useCallback(() => {
    if (!record) return;
    message.info(`列印分析單 ${record.fj750_01}（列印功能尚未實作）`);
  }, [record]);

  const handleFieldChange = useCallback((field: keyof FJ750, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLoadSource = useCallback(async () => {
    const id = sourceId.trim();
    if (!id) {
      message.warning('請先輸入來源分析單號');
      return;
    }
    try {
      const draft = await pwj220Api.loadSourceDraft(id);
      setFormData({ ...draft });
      setDetails(draft.details ? draft.details.map((item) => ({ ...item })) : []);
      setRecord(null);
      toAdd();
      message.success(`已從 ${id} 帶入草稿；尚未儲存`);
    } catch {
      message.error(`找不到來源分析單 ${id}`);
    }
  }, [sourceId, setRecord, toAdd]);

  // Detail operations
  const handleAddDetail = useCallback(() => {
    setDetails(prev => [...prev, { fj751_11: '', fj751_12: '' }]);
  }, []);

  const handleDeleteDetail = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleDetailChange = useCallback((idx: number, field: keyof FJ751, value: any) => {
    setDetails(prev =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  }, []);

  // Load initial list
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <Spin spinning={loading}>
      <div className="pwj220-form">
        {/* Header */}
        <div className="form-header">
          <h2>新產品開發分析單（雙或多層織物）</h2>
          {formData.fj750_01 && (
            <span className="record-id">分析單號：{formData.fj750_01}</span>
          )}
          {formData.fj750_80 === 'P' && (
            <span className="status-approved-badge">已核准</span>
          )}
          <Input.Search
            placeholder="搜尋分析單號/產品代號/名稱"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onSearch={val => handleSearch(val || undefined)}
            style={{ width: 280, marginLeft: 'auto' }}
            disabled={isEditing}
            allowClear
          />
          <Input
            aria-label="來源分析單號"
            placeholder="來源分析單號"
            value={sourceId}
            onChange={event => setSourceId(event.target.value)}
            disabled={isEditing}
            style={{ width: 160 }}
          />
          <Button onClick={handleLoadSource} disabled={isEditing || !permissions.canCreate}>
            從來源單帶入
          </Button>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本資料" key="1">
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
            {/* 洗標明細 on Tab1 */}
            <WashLabelDetail
              details={details}
              isEditing={isEditing}
              onAdd={handleAddDetail}
              onDelete={handleDeleteDetail}
              onChange={handleDetailChange}
            />
          </TabPane>
          <TabPane tab="面布/底布/膜/層數" key="2">
            <Tab2FabricInfo
              formData={formData}
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </TabPane>
          <TabPane tab="貼合/總成本/物化性檢測" key="3">
            <Tab3CostInfo
              formData={formData}
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </TabPane>
          <TabPane tab="備註" key="4">
            <Tab4Remarks
              formData={formData}
              isEditing={isEditing}
              onChange={handleFieldChange}
            />
          </TabPane>
        </Tabs>

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={handleFirst}
          onNavPrev={handlePrev}
          onNavNext={handleNext}
          onNavLast={handleLast}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={handleDelete}
          onSave={handleSave}
          onCancel={handleRevert}
          onPrint={handlePrint}
          onExit={handleExit}
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          acceptanceActionIds={{
            create: 'PWJ220-ACT-CREATE',
            update: 'PWJ220-ACT-UPDATE',
            delete: 'PWJ220-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwj220f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj750_01 ? [record.fj750_01] : []}
          keyOf={(item) => item.fj750_01}
          queryExact={(key) => pwj220Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={handleFirst}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </Spin>
  );
};

export default PWJ220Form;
